import { useState, useRef, useCallback } from 'react';
import {
    UploadCloud, FileText, FileImage, FileType, Paperclip,
    Trash2, RefreshCw, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { notify } from '@/components/core';
import { apiFetch, getStoredToken } from '@/api';
import { useAuth } from '@/context/useAuth';

interface FileObjectNodeConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

// ── File-type metadata ──────────────────────────────────────────────────────
const EXT_META: Record<string, { label: string; Icon: any; color: string; bg: string; border: string }> = {
    pdf:  { label: 'PDF Document',  Icon: FileText,  color: 'text-red-600',    bg: 'from-red-50 to-white',    border: 'border-red-200' },
    docx: { label: 'Word Document', Icon: FileType,  color: 'text-blue-600',   bg: 'from-blue-50 to-white',   border: 'border-blue-200' },
    doc:  { label: 'Word Document', Icon: FileType,  color: 'text-blue-600',   bg: 'from-blue-50 to-white',   border: 'border-blue-200' },
    csv:  { label: 'CSV File',      Icon: FileText,  color: 'text-emerald-600',bg: 'from-emerald-50 to-white',border: 'border-emerald-200' },
    xlsx: { label: 'Excel File',    Icon: FileText,  color: 'text-green-600',  bg: 'from-green-50 to-white',  border: 'border-green-200' },
    xls:  { label: 'Excel File',    Icon: FileText,  color: 'text-green-600',  bg: 'from-green-50 to-white',  border: 'border-green-200' },
    png:  { label: 'PNG Image',     Icon: FileImage, color: 'text-purple-600', bg: 'from-purple-50 to-white', border: 'border-purple-200' },
    jpg:  { label: 'JPEG Image',    Icon: FileImage, color: 'text-purple-600', bg: 'from-purple-50 to-white', border: 'border-purple-200' },
    jpeg: { label: 'JPEG Image',    Icon: FileImage, color: 'text-purple-600', bg: 'from-purple-50 to-white', border: 'border-purple-200' },
    gif:  { label: 'GIF Image',     Icon: FileImage, color: 'text-pink-600',   bg: 'from-pink-50 to-white',   border: 'border-pink-200' },
    webp: { label: 'WebP Image',    Icon: FileImage, color: 'text-pink-600',   bg: 'from-pink-50 to-white',   border: 'border-pink-200' },
    tiff: { label: 'TIFF Image',    Icon: FileImage, color: 'text-indigo-600', bg: 'from-indigo-50 to-white', border: 'border-indigo-200' },
    tif:  { label: 'TIFF Image',    Icon: FileImage, color: 'text-indigo-600', bg: 'from-indigo-50 to-white', border: 'border-indigo-200' },
};

const ALLOWED_EXTS = new Set(['pdf', 'docx', 'doc', 'csv', 'xlsx', 'xls', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'tiff', 'tif']);

function formatBytes(b: number): string {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileObjectNodeConfig = ({ node, onChangeData }: FileObjectNodeConfigProps) => {
    const { token } = useAuth();
    const data = node.data || {};

    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const ext = (data.mimeType?.split('/')?.pop() || data.fileName?.split('.').pop() || '').toLowerCase();
    const meta = EXT_META[ext] ?? {
        label: 'Binary File',
        Icon: Paperclip,
        color: 'text-orange-600',
        bg: 'from-orange-50 to-white',
        border: 'border-orange-200',
    };
    const { Icon, color, bg, border } = meta;

    // ── Upload handler ───────────────────────────────────────────────────────
    const handleUpload = useCallback(async (file: File) => {
        const fileExt = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_EXTS.has(fileExt)) {
            notify.error(`Unsupported file type: .${fileExt}. Allowed: PDF, DOCX, images, CSV, Excel.`);
            return;
        }

        const tok = token || getStoredToken();
        if (!tok) { notify.error('Not logged in.'); return; }

        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const result = await apiFetch('/flows/ops/upload-object', tok, {
                method: 'POST',
                body: form,
            });

            onChangeData({
                filePath:  result.filePath,
                fileName:  result.fileName  ?? file.name,
                mimeType:  result.mimeType  ?? '',
                sizeBytes: result.sizeBytes ?? 0,
                label:     result.fileName  ?? file.name,
            });
            notify.success(`Uploaded "${result.fileName ?? file.name}" (${formatBytes(result.sizeBytes ?? 0)})`);
        } catch (err: any) {
            notify.error(`Upload failed: ${err?.message || 'Unknown error'}`);
        } finally {
            setUploading(false);
        }
    }, [token, onChangeData]);

    const clearFile = () => {
        onChangeData({ filePath: undefined, fileName: undefined, mimeType: undefined, sizeBytes: undefined });
    };

    return (
        <div className="space-y-5">
            {/* ── Section label ──────────────────────────────────────────────── */}
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider block">
                File Object
            </label>

            {/* ── Hidden file input ───────────────────────────────────────────── */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp,.tiff,.tif"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = '';
                }}
            />

            {/* ── No file yet: upload zone ────────────────────────────────────── */}
            {!data.filePath ? (
                <div
                    className={`flex flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed cursor-pointer py-8 px-4 transition-all ${
                        dragOver
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-gray-200 bg-gray-50 hover:border-orange-300 hover:bg-orange-50/40'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        const f = e.dataTransfer.files[0];
                        if (f) handleUpload(f);
                    }}
                >
                    {uploading ? (
                        <>
                            <div className="h-8 w-8 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
                            <span className="text-xs text-orange-600 font-semibold">Uploading…</span>
                        </>
                    ) : (
                        <>
                            <UploadCloud className={`h-10 w-10 ${dragOver ? 'text-orange-500' : 'text-gray-300'}`} />
                            <span className="text-sm font-semibold text-gray-600">Drop a file here</span>
                            <span className="text-xs text-gray-400 text-center leading-relaxed">
                                PDF · DOCX · Images · CSV · Excel<br/>
                                <span className="text-[10px]">Max 20 MB</span>
                            </span>
                            <span className="text-[10px] text-orange-500 underline cursor-pointer">or click to browse</span>
                        </>
                    )}
                </div>
            ) : (
                <>
                    {/* ── File card ──────────────────────────────────────────────── */}
                    <div className={`rounded-xl border bg-gradient-to-br ${bg} ${border} p-3 space-y-3`}>
                        {/* Title row */}
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-white border border-gray-100 shadow-sm flex-shrink-0">
                                <Icon className={`h-5 w-5 ${color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate" title={data.fileName}>
                                    {data.fileName}
                                </p>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide">{meta.label}</p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-100 transition-colors"
                                    title="Replace file"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Remove file"
                                    onClick={clearFile}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {data.sizeBytes > 0 && (
                                <span className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
                                    {formatBytes(data.sizeBytes)}
                                </span>
                            )}
                            {data.mimeType && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white border ${border} ${color}`}>
                                    {data.mimeType}
                                </span>
                            )}
                        </div>

                        {/* S3 path (subtle) */}
                        {data.filePath && (
                            <div className="rounded-md bg-white/70 border border-gray-100 px-2 py-1.5">
                                <p className="text-[9px] text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Stored at</p>
                                <p className="text-[10px] text-gray-500 font-mono truncate" title={data.filePath}>
                                    {data.filePath}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* ── Output schema info ─────────────────────────────────────── */}
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Output Schema (1 row)
                        </p>
                        <div className="space-y-1">
                            {[
                                { col: 'file_path',      type: 'string' },
                                { col: 'file_name',      type: 'string' },
                                { col: 'mime_type',      type: 'string' },
                                { col: 'size_bytes',     type: 'int64' },
                                { col: 'extracted_text', type: 'string' },
                            ].map(({ col, type }) => (
                                <div key={col} className="flex items-center justify-between text-[10px] px-1">
                                    <span className={`font-mono ${col === 'extracted_text' ? 'text-emerald-700 font-semibold' : 'text-gray-700'}`}>{col}</span>
                                    <span className="text-blue-400 font-mono">{type}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Usage guide ────────────────────────────────────────────── */}
                    <div className="rounded-lg border border-orange-100 bg-orange-50/60 p-3 space-y-2">
                        <p className="text-[10px] font-bold text-orange-800 uppercase tracking-wide flex items-center gap-1.5">
                            <AlertTriangle className="h-3 w-3" /> Downstream Usage
                        </p>
                        <div className="space-y-1.5 text-[10px] text-orange-700 leading-relaxed">
                            <p>
                                <strong>→ LLM / Agentic Insight:</strong> Connect directly — reads <code className="bg-orange-100 px-0.5 rounded">extracted_text</code> automatically. No extra config needed.
                            </p>
                            <p>
                                <strong>→ Document Parser:</strong> Uses <code className="bg-orange-100 px-0.5 rounded">file_path</code> for its own deep parse if you need structured sections/tables.
                            </p>
                            <p>
                                <strong>→ Python Script:</strong> <code className="bg-orange-100 px-0.5 rounded">df["extracted_text"].iloc[0]</code> gives the full text. Use <code className="bg-orange-100 px-0.5 rounded">df["file_path"].iloc[0]</code> for raw S3 bytes via boto3.
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FileObjectNodeConfig;
